import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Timeline as TimelineIcon,
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Bar, Line } from 'react-chartjs-2';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';
import '../utils/chartSetup';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010';

// Types
interface RawAgentRecord {
  Date?: string;
  Agent?: string;
  agent?: string;
  Calls?: string | number;
  calls?: string | number;
  Contacts?: string | number;
  contacts?: string | number;
  'Talk %'?: string | number;
  'talk %'?: string | number;
  'Talk Time'?: string | number;
  'talk time'?: string | number;
  'ACW %'?: string | number;
  'acw %'?: string | number;
  ACW?: string | number;
  acw?: string | number;
  AHT?: string | number;
  aht?: string | number;
  'Avg Handle Time'?: string | number;
  'avg handle time'?: string | number;
  Utilization?: string | number;
  utilization?: string | number;
  'Utilization %'?: string | number;
  Sales?: string | number;
  sales?: string | number;
  SPH?: string | number;
  sph?: string | number;
  'Win Rate'?: string | number;
  'win rate'?: string | number;
  'Login Time'?: string;
  'login time'?: string;
}

interface AgentDataResponse {
  data?: RawAgentRecord[];
  totalRecords?: number;
}

interface AgentMetricsResponse {
  lastUpdated?: string;
}

interface FilteredMetrics {
  totalAgents: number;
  totalCalls: number;
  totalContacts: number;
  avgTalkPercent: string | number;
  avgACWPercent: string | number;
  avgAHT: string | number;
  avgUtilization: string | number;
  totalSales: number;
  avgSPH: string | number;
  avgWinRate: string | number;
}

interface AggregatedAgent {
  Agent: string;
  Calls: number;
  Contacts: number;
  'Login Time': string;
  'Talk %': string;
  'ACW %': string;
  AHT: string;
  Utilization: string;
  Sales: number;
  'Win Rate': string;
}

interface TopPerformer {
  name: string;
  sales: number;
}

interface TrendData {
  date: string;
  sales: number;
  calls: number;
}

type DateFilter = 'today' | 'previousDay' | 'currentWeek' | 'previousWeek' | 'currentMonth' | 'previousMonth' | 'dateRange' | 'all';
type SortOrder = 'asc' | 'desc';

const PartnerEfficiency: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>('currentWeek');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [orderBy, setOrderBy] = useState<keyof AggregatedAgent>('Sales');
  const [order, setOrder] = useState<SortOrder>('desc');

  // Partner User names — users whose permission_group_id = 10 (Partner User)
  const [partnerUserNames, setPartnerUserNames] = useState<Set<string>>(new Set());
  const [partnerUsersLoading, setPartnerUsersLoading] = useState(true);

  useEffect(() => {
    const fetchPartnerUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/user-management/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (data.success) {
          const names = new Set<string>(
            data.users
              .filter((u: any) => u.permission_group_id === 10)
              .map((u: any) => u.username as string)
          );
          setPartnerUserNames(names);
        }
      } catch (e) {
        console.error('Failed to fetch partner users:', e);
      } finally {
        setPartnerUsersLoading(false);
      }
    };
    fetchPartnerUsers();
  }, []);

  const {
    data: agentData,
    isLoading: dataLoading,
    error: dataError,
  } = useQuery({
    queryKey: ['partnerEfficiencyData'],
    queryFn: api.getAgentEfficiencyData,
    refetchInterval: 5 * 60 * 1000,
  });

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery({
    queryKey: ['partnerEfficiencyMetrics'],
    queryFn: api.getAgentEfficiencyMetrics,
    refetchInterval: 5 * 60 * 1000,
  });

  const agentInfo: AgentDataResponse = agentData?.data || {};
  const rawData: RawAgentRecord[] = agentInfo.data || [];
  const metrics: AgentMetricsResponse = metricsData?.metrics || {};

  const parseSheetDate = useCallback((dateString?: string): Date | null => {
    if (!dateString) return null;
    try {
      let dateStr = dateString;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        dateStr = dateString + 'T00:00:00';
      }
      const parsedDate = new Date(dateStr);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const currentYear = new Date().getFullYear();
        const parsedYear = parsedDate.getFullYear();
        if (parsedYear >= 2020 && parsedYear <= currentYear + 1) {
          return parsedDate;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  const normalizeDate = useCallback((date?: Date | string | null): string | null => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Filter by date AND by partner user names
  const filteredData = useMemo((): RawAgentRecord[] => {
    let data = dateFilter === 'all' ? rawData : rawData.filter(record => {
      if (!record.Date) return false;
      const recordDate = parseSheetDate(record.Date);
      if (!recordDate || isNaN(recordDate.getTime())) return false;
      const normalizedRecordDate = normalizeDate(recordDate);
      const now = new Date();

      switch (dateFilter) {
        case 'today':
          return normalizedRecordDate === normalizeDate(now);
        case 'previousDay':
          return normalizedRecordDate === normalizeDate(subDays(now, 1));
        case 'currentWeek': {
          const weekStart = normalizeDate(startOfWeek(now, { weekStartsOn: 1 }));
          const weekEnd = normalizeDate(endOfWeek(now, { weekStartsOn: 1 }));
          return normalizedRecordDate! >= weekStart! && normalizedRecordDate! <= weekEnd!;
        }
        case 'previousWeek': {
          const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
          const prevWeekStart = normalizeDate(subWeeks(currentWeekStart, 1));
          const prevWeekEnd = normalizeDate(subDays(currentWeekStart, 1));
          return normalizedRecordDate! >= prevWeekStart! && normalizedRecordDate! <= prevWeekEnd!;
        }
        case 'currentMonth': {
          const monthStart = normalizeDate(startOfMonth(now));
          const monthEnd = normalizeDate(endOfMonth(now));
          return normalizedRecordDate! >= monthStart! && normalizedRecordDate! <= monthEnd!;
        }
        case 'previousMonth': {
          const prevMonthStart = normalizeDate(startOfMonth(subMonths(now, 1)));
          const prevMonthEnd = normalizeDate(endOfMonth(subMonths(now, 1)));
          return normalizedRecordDate! >= prevMonthStart! && normalizedRecordDate! <= prevMonthEnd!;
        }
        case 'dateRange':
          if (!startDate || !endDate) return false;
          const [sy, sm, sd] = startDate.split('-').map(Number);
          const [ey, em, ed] = endDate.split('-').map(Number);
          const rangeStart = normalizeDate(new Date(sy, sm - 1, sd));
          const rangeEnd = normalizeDate(new Date(ey, em - 1, ed));
          return normalizedRecordDate! >= rangeStart! && normalizedRecordDate! <= rangeEnd!;
        default:
          return true;
      }
    });

    // Filter to only Partner User group agents (if we have the list)
    if (partnerUserNames.size > 0) {
      data = data.filter(record => {
        const agentName = record.Agent || record.agent || '';
        return partnerUserNames.has(agentName);
      });
    }

    return data;
  }, [rawData, dateFilter, startDate, endDate, parseSheetDate, normalizeDate, partnerUserNames]);

  const filteredMetrics = useMemo((): FilteredMetrics => {
    if (filteredData.length === 0) {
      return { totalAgents: 0, totalCalls: 0, totalContacts: 0, avgTalkPercent: 0, avgACWPercent: 0, avgAHT: 0, avgUtilization: 0, totalSales: 0, avgSPH: 0, avgWinRate: 0 };
    }
    let totalCalls = 0, totalContacts = 0, totalTalkPercent = 0, totalACWPercent = 0;
    let totalAHT = 0, totalUtilization = 0, totalSales = 0, totalSPH = 0, totalWinRate = 0, validRecords = 0;
    const uniqueAgents = new Set<string>();

    filteredData.forEach(row => {
      uniqueAgents.add(row.Agent || row.agent || 'Unknown');
      const calls = parseFloat(String(row.Calls || row.calls || 0));
      const contacts = parseFloat(String(row.Contacts || row.contacts || 0));
      const talkPercent = parseFloat(String(row['Talk %'] || row['talk %'] || row['Talk Time'] || row['talk time'] || '0').replace('%', ''));
      const acwPercent = parseFloat(String(row['ACW %'] || row['acw %'] || row['ACW'] || row['acw'] || '0').replace('%', ''));
      const aht = parseFloat(String(row.AHT || row.aht || 0));
      const utilization = parseFloat(String(row.Utilization || row.utilization || '0').replace('%', ''));
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      const sph = parseFloat(String(row.SPH || row.sph || 0));
      const winRate = parseFloat(String(row['Win Rate'] || row['win rate'] || '0').replace('%', ''));
      if (!isNaN(calls)) totalCalls += calls;
      if (!isNaN(contacts)) totalContacts += contacts;
      if (!isNaN(talkPercent)) { totalTalkPercent += talkPercent; validRecords++; }
      if (!isNaN(acwPercent)) totalACWPercent += acwPercent;
      if (!isNaN(aht)) totalAHT += aht;
      if (!isNaN(utilization)) totalUtilization += utilization;
      if (!isNaN(sales)) totalSales += sales;
      if (!isNaN(sph)) totalSPH += sph;
      if (!isNaN(winRate)) totalWinRate += winRate;
    });

    return {
      totalAgents: uniqueAgents.size,
      totalCalls: Math.round(totalCalls),
      totalContacts: Math.round(totalContacts),
      avgTalkPercent: validRecords > 0 ? (totalTalkPercent / validRecords).toFixed(2) : 0,
      avgACWPercent: validRecords > 0 ? (totalACWPercent / validRecords).toFixed(2) : 0,
      avgAHT: validRecords > 0 ? (totalAHT / validRecords).toFixed(2) : 0,
      avgUtilization: validRecords > 0 ? (totalUtilization / validRecords).toFixed(2) : 0,
      totalSales: Math.round(totalSales),
      avgSPH: validRecords > 0 ? (totalSPH / validRecords).toFixed(2) : 0,
      avgWinRate: validRecords > 0 ? (totalWinRate / validRecords).toFixed(2) : 0,
    };
  }, [filteredData]);

  const topPerformersData = useMemo((): TopPerformer[] => {
    const agentStats: { [key: string]: TopPerformer } = {};
    filteredData.forEach(row => {
      const agent = row.Agent || row.agent || 'Unknown';
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      if (!agentStats[agent]) agentStats[agent] = { name: agent, sales: 0 };
      agentStats[agent].sales += sales;
    });
    return Object.values(agentStats).sort((a, b) => b.sales - a.sales).slice(0, 10);
  }, [filteredData]);

  const trendData = useMemo((): TrendData[] => {
    const dateStats: { [key: string]: TrendData } = {};
    filteredData.forEach(row => {
      const date = row.Date || '';
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      const calls = parseFloat(String(row.Calls || row.calls || 0));
      if (!dateStats[date]) dateStats[date] = { date, sales: 0, calls: 0 };
      dateStats[date].sales += sales;
      dateStats[date].calls += calls;
    });
    return Object.values(dateStats).sort((a, b) => {
      const dateA = parseSheetDate(a.date);
      const dateB = parseSheetDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });
  }, [filteredData, parseSheetDate]);

  const aggregatedData = useMemo((): AggregatedAgent[] => {
    const agentMap: { [key: string]: any } = {};
    filteredData.forEach(row => {
      const agent = row.Agent || row.agent || 'Unknown';
      if (!agentMap[agent]) {
        agentMap[agent] = { Agent: agent, Calls: 0, Contacts: 0, 'Login Time': '', 'Talk %': 0, 'ACW %': 0, AHT: 0, Utilization: 0, Sales: 0, 'Win Rate': 0, count: 0, totalLoginMinutes: 0 };
      }
      const a = agentMap[agent];
      a.Calls += parseFloat(String(row.Calls || row.calls || 0));
      a.Contacts += parseFloat(String(row.Contacts || row.contacts || 0));
      a['Talk %'] += parseFloat(String(row['Talk %'] || row['talk %'] || row['Talk Time'] || row['talk time'] || '0').replace('%', ''));
      a['ACW %'] += parseFloat(String(row['ACW %'] || row['acw %'] || row['ACW'] || row['acw'] || '0').replace('%', ''));
      a.AHT += parseFloat(String(row.AHT || row.aht || row['Avg Handle Time'] || row['avg handle time'] || 0));
      a.Utilization += parseFloat(String(row.Utilization || row.utilization || row['Utilization %'] || '0').replace('%', ''));
      a.Sales += parseFloat(String(row.Sales || row.sales || 0));
      a['Win Rate'] += parseFloat(String(row['Win Rate'] || row['win rate'] || '0').replace('%', ''));
      a.count++;
      const loginTime = row['Login Time'] || row['login time'] || '0:00:00';
      const timeParts = loginTime.split(':');
      if (timeParts.length >= 2) {
        a.totalLoginMinutes += (parseInt(timeParts[0]) || 0) * 60 + (parseInt(timeParts[1]) || 0);
      }
    });
    return Object.values(agentMap).map((a: any) => ({
      Agent: a.Agent,
      Calls: Math.round(a.Calls),
      Contacts: Math.round(a.Contacts),
      'Login Time': `${Math.floor(a.totalLoginMinutes / 60)}:${(a.totalLoginMinutes % 60).toString().padStart(2, '0')}`,
      'Talk %': a.count > 0 ? `${(a['Talk %'] / a.count).toFixed(2)}%` : '0%',
      'ACW %': a.count > 0 ? `${(a['ACW %'] / a.count).toFixed(2)}%` : '0%',
      AHT: a.count > 0 ? (a.AHT / a.count).toFixed(2) : '0.00',
      Utilization: a.count > 0 ? `${(a.Utilization / a.count).toFixed(2)}%` : '0%',
      Sales: Math.round(a.Sales),
      'Win Rate': a.count > 0 ? `${(a['Win Rate'] / a.count).toFixed(2)}%` : '0%',
    }));
  }, [filteredData]);

  const handleSort = useCallback((property: keyof AggregatedAgent) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, order]);

  const sortedData = useMemo((): AggregatedAgent[] => {
    return [...aggregatedData].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      if (typeof aValue === 'string' && aValue.includes('%')) aValue = parseFloat(aValue.replace('%', ''));
      if (typeof bValue === 'string' && bValue.includes('%')) bValue = parseFloat(bValue.replace('%', ''));
      aValue = isNaN(parseFloat(String(aValue))) ? aValue : parseFloat(String(aValue));
      bValue = isNaN(parseFloat(String(bValue))) ? bValue : parseFloat(String(bValue));
      if (order === 'asc') return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      else return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });
  }, [aggregatedData, orderBy, order]);

  const paginatedData = useMemo((): AggregatedAgent[] => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const formatNumber = useCallback((value: number): string => new Intl.NumberFormat('en-US').format(value), []);

  const handleDateFilterChange = useCallback((event: SelectChangeEvent<DateFilter>) => {
    setDateFilter(event.target.value as DateFilter);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const getFilterLabel = useCallback((): string => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case 'previousDay': return 'Previous Day';
      case 'currentWeek': return 'Current Week';
      case 'previousWeek': return 'Previous Week';
      case 'currentMonth': return 'Current Month';
      case 'previousMonth': return 'Previous Month';
      case 'dateRange':
        if (startDate && endDate) {
          const start = format(new Date(startDate), 'MMM dd, yyyy');
          const end = format(new Date(endDate), 'MMM dd, yyyy');
          return startDate === endDate ? start : `${start} - ${end}`;
        }
        return 'Date Range';
      case 'all': return 'All Time';
      default: return '';
    }
  }, [dateFilter, startDate, endDate]);

  if (dataLoading || metricsLoading || partnerUsersLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (dataError || metricsError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load partner efficiency data. Please check your connection and try again.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4">
            Partner Efficiency Analysis
          </Typography>
          <Chip label="Weekend Warriors — Partner User Group" sx={{ mt: 0.5, bgcolor: '#ff9800', color: 'white', fontWeight: 600 }} />
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select value={dateFilter} onChange={handleDateFilterChange} displayEmpty>
              <MenuItem value="" disabled><em>Select Time Period</em></MenuItem>
              <MenuItem value="today">Current Day</MenuItem>
              <MenuItem value="previousDay">Previous Day</MenuItem>
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="currentMonth">Current Month</MenuItem>
              <MenuItem value="previousMonth">Previous Month</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'dateRange' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">From:</Typography>
              <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }} inputProps={{ max: endDate || undefined }} />
              <Typography variant="body2">To:</Typography>
              <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }} inputProps={{ min: startDate || undefined }} />
            </Box>
          )}
        </Box>
      </Box>

      {partnerUserNames.size === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No users are currently assigned to the Partner User permission group. Assign users to the "Partner User" group in User Management to see their data here.
        </Alert>
      )}

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Active Agents</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.totalAgents || 0)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PhoneIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Total Calls</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.totalCalls || 0)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Total Contacts</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.totalContacts || 0)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrophyIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Total Sales</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.totalSales || 0)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SpeedIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Avg Win Rate</Typography>
                  <Typography variant="h5">{filteredMetrics.avgWinRate || 0}%</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimelineIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Avg Talk %</Typography>
                  <Typography variant="h5">{filteredMetrics.avgTalkPercent || 0}%</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccessTimeIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Avg ACW %</Typography>
                  <Typography variant="h5">{filteredMetrics.avgACWPercent || 0}%</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Top Performers Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Top Performers by Sales</Typography>
            <Box height={300}>
              {topPerformersData.length > 0 ? (
                <Bar
                  data={{
                    labels: topPerformersData.map(p => p.name),
                    datasets: [{ label: 'Sales', data: topPerformersData.map(p => p.sales), backgroundColor: 'rgba(255, 152, 0, 0.6)', borderColor: 'rgba(255, 152, 0, 1)', borderWidth: 1 }],
                  }}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Trend Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Sales & Calls Trend</Typography>
            <Box height={300}>
              {trendData.length > 0 ? (
                <Line
                  data={{
                    labels: trendData.map(t => { const date = parseSheetDate(t.date); return date ? format(date, 'MMM dd') : t.date; }),
                    datasets: [
                      { label: 'Sales', data: trendData.map(t => t.sales), borderColor: 'rgba(75, 192, 192, 1)', backgroundColor: 'rgba(75, 192, 192, 0.2)', yAxisID: 'y', tension: 0.1 },
                      { label: 'Calls', data: trendData.map(t => t.calls), borderColor: 'rgba(255, 99, 132, 1)', backgroundColor: 'rgba(255, 99, 132, 0.2)', yAxisID: 'y1', tension: 0.1 },
                    ],
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false,
                    interaction: { mode: 'index' as const, intersect: false },
                    plugins: { legend: { position: 'top' as const } },
                    scales: {
                      y: { type: 'linear' as const, display: true, position: 'left' as const, title: { display: true, text: 'Sales' } },
                      y1: { type: 'linear' as const, display: true, position: 'right' as const, title: { display: true, text: 'Calls' }, grid: { drawOnChartArea: false } },
                    },
                  }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Detailed Data Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Detailed Agent Data — Partner Users</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(['Agent', 'Calls', 'Contacts', 'Login Time', 'Talk %', 'ACW %', 'AHT', 'Utilization', 'Sales', 'Win Rate'] as (keyof AggregatedAgent)[]).map(col => (
                      <TableCell key={col} align={col === 'Agent' ? 'left' : 'right'}>
                        <TableSortLabel active={orderBy === col} direction={orderBy === col ? order : 'asc'} onClick={() => handleSort(col)}>
                          {col}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{row.Agent}</TableCell>
                        <TableCell align="right">{row.Calls}</TableCell>
                        <TableCell align="right">{row.Contacts}</TableCell>
                        <TableCell align="right">{row['Login Time']}</TableCell>
                        <TableCell align="right">{row['Talk %']}</TableCell>
                        <TableCell align="right">{row['ACW %']}</TableCell>
                        <TableCell align="right">{row.AHT}</TableCell>
                        <TableCell align="right">{row.Utilization}</TableCell>
                        <TableCell align="right">{row.Sales}</TableCell>
                        <TableCell align="right">{row['Win Rate']}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="textSecondary">No partner user data available for this period</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PartnerEfficiency;
